import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Sparkles, Layers, Pause, Play, RotateCcw, ArrowRight } from 'lucide-react';
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap-config';
import { StudioSequence } from './StudioSequence';
import './idea-flow.css';

interface IdeaPreset {
  id: string;
  prompt: string;
  slides: { title: string; points: string[]; tag: string }[];
}
const seed = 'M12 2L22 12L12 22L2 12Z';
const pages = 'M3 3H21V7H3Z M3 10H21V14H3Z M3 17H21V21H3Z';

export function IdeaFlowDemo({ preset }: { preset: IdeaPreset }) {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const userPaused = useRef(false);
  const [paused, setPaused] = useState(false);
  const [replay, setReplay] = useState(0);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const element = root.current!;
      const routes = Array.from(element.querySelectorAll<SVGPathElement>('.if-route'));
      const packets = element.querySelectorAll('.if-packet');
      const tl = gsap.timeline({ paused: true, defaults: { ease: 'power2.inOut' }, onUpdate: () => {
        const phase = tl.time() < 1.5 ? '0' : tl.time() < 4.2 ? '1' : '2';
        element.dataset.phase = phase;
      } });
      tl.fromTo('.if-prompt-text', { opacity: 0.45 }, { opacity: 1, duration: 1.2 }, 0)
        .fromTo('.if-scan', { scaleX: 0 }, { scaleX: 1, duration: 1.3, ease: 'none' }, 0)
        .fromTo('.if-core-shape', { morphSVG: seed }, { morphSVG: pages, duration: 1.4 }, 1.2)
        .fromTo('.if-idea', { y: 16, opacity: 0.12 }, { y: 0, opacity: 1, duration: 0.7, stagger: 0.65 }, 1.5);
      routes.forEach((route, index) => {
        const at = 2.2 + index * 0.65;
        tl.fromTo(route, { drawSVG: '0% 0%' }, { drawSVG: '0% 100%', duration: 0.8 }, at)
          .fromTo(packets[index], { opacity: 1 }, { motionPath: { path: route, align: route, alignOrigin: [0.5, 0.5] }, duration: 0.8 }, at)
          .to(packets[index], { opacity: 0, duration: 0.1 }, at + 0.8);
      });
      tl.fromTo('.if-slide', { clipPath: 'inset(0% 100% 0% 0% round 10px)', y: 12 },
        { clipPath: 'inset(0% 0% 0% 0% round 10px)', y: 0, duration: 0.85, stagger: 0.65 }, 3.1)
        .fromTo('.if-complete', { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.6 }, 5.6)
        .fromTo('.if-progress', { scaleX: 0 }, { scaleX: 1, duration: 6.4, ease: 'none' }, 0);
      let visible = false;
      const sync = () => tl.paused(!visible || document.hidden || userPaused.current);
      const trigger = ScrollTrigger.create({ trigger: element, start: 'top 80%', end: 'bottom 15%',
        onToggle: (self) => { visible = self.isActive; sync(); },
      });
      visible = trigger.isActive;
      sync();
      element.addEventListener('flow-playback', sync);
      document.addEventListener('visibilitychange', sync);
      return () => {
        delete element.dataset.phase;
        element.removeEventListener('flow-playback', sync);
        document.removeEventListener('visibilitychange', sync);
      };
    });
    return () => media.revert();
  }, { scope: root, dependencies: [preset.id, i18n.resolvedLanguage, replay], revertOnUpdate: true });

  const toggle = () => {
    userPaused.current = !userPaused.current;
    setPaused(userPaused.current);
    root.current?.dispatchEvent(new Event('flow-playback'));
  };
  const restart = () => { userPaused.current = false; setPaused(false); setReplay((value) => value + 1); };

  return <div ref={root} className="idea-flow">
    <div className="if-top"><span>{t('lab.demoLabel')}</span><div className="if-controls"><button onClick={toggle} aria-label={t(paused ? 'lab.resumeDemo' : 'lab.pauseDemo')}>{paused ? <Play size={13} /> : <Pause size={13} />}{t(paused ? 'lab.resumeDemo' : 'lab.pauseDemo')}</button><button onClick={restart}><RotateCcw size={13} aria-hidden="true" />{t('lab.replayDemo')}</button></div></div>
    <div className="if-stage">
      <StudioSequence variant="flow" />
      <section className="if-column if-input"><header><span className="if-step">01</span><FileText size={15} aria-hidden="true" /><h3>{t('lab.demoInput')}</h3></header><p className="if-help">{t('lab.demoInputHelp')}</p><div className="if-paper"><span className="if-paper-label">{t('lab.inputTitle')}</span><p className="if-prompt-text">“{preset.prompt}”</p><div className="if-scan" aria-hidden="true" /></div><p className="if-caption">{t('lab.demoInputCaption')}</p></section>
      <section className="if-column if-analysis"><header><span className="if-step">02</span><Sparkles size={15} aria-hidden="true" /><h3>{t('lab.demoAnalysis')}</h3></header><p className="if-help">{t('lab.demoAnalysisHelp')}</p><div className="if-core" aria-hidden="true"><svg viewBox="0 0 24 24"><path className="if-core-shape" d={pages} fill="currentColor" /></svg></div><ol className="if-ideas">{preset.slides.map((slide, index) => <li className="if-idea" key={index}><span>0{index + 1}</span><p>{slide.title}</p><ArrowRight size={12} aria-hidden="true" /></li>)}</ol></section>
      <svg className="if-routing" viewBox="0 0 100 300" fill="none" aria-hidden="true">{[55, 150, 245].map((y, index) => <g key={y}><path className="if-route" d={`M2 ${y}C40 ${y} 60 ${y} 98 ${y}`} stroke="currentColor" strokeWidth="1.5" /><circle className="if-packet" r="3" fill="currentColor" /></g>)}</svg>
      <section className="if-column if-output"><header><span className="if-step">03</span><Layers size={15} aria-hidden="true" /><h3>{t('lab.demoOutput')}</h3></header><p className="if-help">{t('lab.demoOutputHelp')}</p><div className="if-slides">{preset.slides.map((slide, index) => <article className={`if-slide if-slide-${index}`} key={index}><div className="if-slide-top"><span>0{index + 1} / 03</span><span>16:9</span></div><h4>{slide.title}</h4><ul>{slide.points.map((point) => <li key={point}>{point}</li>)}</ul><div className="if-slide-line" aria-hidden="true" /></article>)}</div></section>
    </div>
    <div className="if-footer"><p>{t('lab.demoMapping')}</p><span className="if-complete">{t('lab.demoComplete')}</span></div><div className="if-progress" aria-hidden="true" />
  </div>;
}
